
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Images } from '../assets/assets';
import { Tab, Nav, Table } from 'react-bootstrap';
import CustomModal from '../components/Shared/CustomModal/CustomModal';
import scanService from '../services/scanService';
import patientService from '../services/patientService';
import './Reports.css';

export default function Reports() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientScans, setPatientScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [allScansData, setAllScansData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [scansLoading, setScansLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('patient-summary');

  // Fetch patients on mount; for non-admin select current user automatically
  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      // Fetch patients using the new patient service
      // This will automatically filter by createdBy for medical users
      const response = await patientService.getPatientsWithLatestScan({ limit: 1000 });

      // Handle response - response.data should be the patients array
      const patientsArray = response.data || [];

      if (patientsArray.length > 0) {
        // Map patient data to expected format
        const mappedPatients = patientsArray.map(patient => ({
          id: patient.id || patient._id,
          _id: patient.id || patient._id,
          patientId: patient.patientId,
          name: patient.name,
          username: patient.name, // Keep for backward compatibility in reports
          email: patient.email,
          latestScan: patient.latestScan,
          scanCount: patient.scanCount
        }));

        setPatients(mappedPatients);

        // Don't auto-select - let user choose
      } else {
        setPatients([]);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllScansData = async () => {
    try {
      setScansLoading(true);
      const allScans = [];

      // Fetch scans for each patient
      for (const patient of patients) {
        try {
          const response = await scanService.getScansByPatient(patient.id);

          if (response.data && response.data.length > 0) {
            // Add patient info to each scan
            const scansWithPatientInfo = response.data.map(scan => ({
              ...scan,
              patientId: patient.patientId || patient.id,
              patientName: patient.name,
              // Patient info from latest scan if available
              patientAge: scan.age || (patient.latestScan?.age),
              patientSex: scan.sex || (patient.latestScan?.sex),
              patientDiagnosis: scan.msSubType || (patient.latestScan?.msSubType)
            }));
            allScans.push(...scansWithPatientInfo);
          } else {
            console.log(`No scans found for patient ${patient.id}`);
          }
        } catch (error) {
          console.error(`Error fetching scans for patient ${patient.id}:`, error);
        }
      }

      setAllScansData(allScans);
    } catch (error) {
      console.error('Error fetching all scans data:', error);
    } finally {
      setScansLoading(false);
    }
  };

  const handlePatientSelect = async (patientId) => {
    if (!patientId) {
      // Clear selection if empty value selected
      setSelectedPatient(null);
      setPatientScans([]);
      return;
    }

    try {
      setLoading(true);
      const patient = patients.find(p => p.id === patientId);

      if (!patient) {
        console.error('Patient not found:', patientId);
        return;
      }

      setSelectedPatient(patient);

      // Fetch patient scans
      const response = await scanService.getScansByPatient(patientId);
      if (response.data) {
        setPatientScans(response.data);
        if (response.data.length > 0) {
        }
      }
    } catch (error) {
      console.error('Error fetching patient scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = () => {
    if (!selectedPatient || !patientScans.length) return;

    // Get latest scan data
    const latestScan = patientScans[0];
    const analysisResult = latestScan?.analysisResult;
    const consolidatedResult = analysisResult?.consolidated_results?.[0];
    const biomarkers = consolidatedResult?.biomarkers || {};
    const patientInfo = consolidatedResult?.patientInformation || {};

    // Process longitudinal data
    const longitudinalData = patientScans.map(scan => {
      const scanAnalysis = scan.analysisResult;
      const scanConsolidated = scanAnalysis?.consolidated_results?.[0];
      const scanBiomarkers = scanConsolidated?.biomarkers || {};

      return {
        timepoint: scanAnalysis?.timepoint || 'N/A',
        date: scanAnalysis?.analysis_timestamp ?
          new Date(scanAnalysis.analysis_timestamp).toISOString().split('T')[0] : 'N/A',
        brainVolume: scanBiomarkers.totalBrainVolume_mm3 ?
          (Number(scanBiomarkers.totalBrainVolume_mm3) / 1000).toFixed(2) : 'N/A',
        lesionVolume: scanBiomarkers.totalLesionVolume_mm3 ?
          (Number(scanBiomarkers.totalLesionVolume_mm3) / 1000).toFixed(2) : 'N/A',
        t1Intensity: scanBiomarkers.averageT1BlackHoleIntensity ?
          Number(scanBiomarkers.averageT1BlackHoleIntensity).toFixed(2) : 'N/A'
      };
    });

    const reportData = {
      patient: {
        id: patientInfo.patientId || selectedPatient.patientId || selectedPatient.id,
        name: selectedPatient.name || selectedPatient.username,
        age: patientInfo.ageAtScan || (latestScan?.age) || 'N/A',
        sex: patientInfo.sex === 'M' ? 'Male' : patientInfo.sex === 'F' ? 'Female' : patientInfo.sex || (latestScan?.sex === 'M' ? 'Male' : latestScan?.sex === 'F' ? 'Female' : latestScan?.sex || 'N/A'),
        diagnosis: patientInfo.msSubtype || (latestScan?.msSubType) || 'Multiple Sclerosis',
        totalScans: patientScans.length
      },
      latestScan: {
        timepoint: analysisResult?.timepoint || 'N/A',
        scanDate: analysisResult?.analysis_timestamp ?
          new Date(analysisResult.analysis_timestamp).toISOString().split('T')[0] : 'N/A',
        segmentation: consolidatedResult?.segmentation_method || 'Predicted (Ensemble)',
        totalBrainVolume: biomarkers.totalBrainVolume_mm3 ?
          (Number(biomarkers.totalBrainVolume_mm3) / 1000).toFixed(2) : 'N/A',
        totalLesionVolume: biomarkers.totalLesionVolume_mm3 ?
          (Number(biomarkers.totalLesionVolume_mm3) / 1000).toFixed(2) : 'N/A',
        whiteMatterLesions: biomarkers.lesionVolumeInWhiteMatter_mm3 ?
          (Number(biomarkers.lesionVolumeInWhiteMatter_mm3) / 1000).toFixed(2) : 'N/A',
        greyMatterLesions: biomarkers.lesionVolumeInGreyMatter_mm3 ?
          (Number(biomarkers.lesionVolumeInGreyMatter_mm3) / 1000).toFixed(2) : 'N/A',
        t1Intensity: biomarkers.averageT1BlackHoleIntensity ?
          Number(biomarkers.averageT1BlackHoleIntensity).toFixed(2) : 'N/A'
      },
      longitudinalData: longitudinalData,
      reportDate: new Date().toLocaleDateString('en-GB')
    };

    setReportData(reportData);
    setShowReportModal(true);
  };

  const printReport = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');

    // Get the report content
    const reportContent = document.querySelector('.report-content');

    if (reportContent) {
      // Create the print HTML
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Patient Summary Report - ${reportData?.patient?.name || 'Unknown'}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              color: #2c3e50;
            }
            .report-content {
              max-width: 800px;
              margin: 0 auto;
            }
            .report-subtitle {
              font-size: 28px;
              color: #6c757d;
              margin-bottom: 25px;
              font-weight: 500;
            }
            .report-section {
              margin-bottom: 30px;
            }
            .section-header {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .section-header h4 {
              font-size: 1.3rem;
              font-weight: 600;
              color: #2c3e50;
              margin: 0;
            }
            .patient-basic-details {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .details-column {
              display: flex;
              flex-direction: column;
              gap: 12px;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              gap:10px
              align-items: center;
              padding: 8px 0;
            }
            .detail-label {
              font-weight: 600;
              color: #495057;
              font-size: 0.95rem;
            }
            .detail-value {
              font-weight: 500;
              color: #2c3e50;
              font-size: 0.95rem;
            }
            .scan-summary {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .scan-info {
              margin-bottom: 20px;
            }
            .info-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 8px 0;
              border-bottom: 1px solid #e9ecef;
            }
            .info-item:last-child {
              border-bottom: none;
            }
            .info-label {
              font-weight: 600;
              color: #495057;
              font-size: 0.95rem;
            }
            .info-value {
              font-weight: 500;
              color: #2c3e50;
              font-size: 0.95rem;
            }
            .segmentation-badge {
              background: #007bff;
              color: white;
              padding: 4px 12px;
              border-radius: 20px;
              font-size: 0.85rem;
              font-weight: 500;
            }
            .metrics-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
            }
            .metric-item {
              background: white;
              padding: 15px;
              border-radius: 8px;
              border: 1px solid #e9ecef;
              text-align: center;
            }
            .metric-label {
              display: block;
              font-weight: 600;
              color: #495057;
              font-size: 0.9rem;
              margin-bottom: 8px;
            }
            .metric-value {
              display: block;
              font-weight: 700;
              color: #2c3e50;
              font-size: 1.1rem;
            }
            .warning-box {
              background: #fff3cd;
              border: 1px solid #ffeaa7;
              border-radius: 8px;
              padding: 15px;
              display: flex;
              align-items: flex-start;
              gap: 12px;
            }
            .warning-icon {
              font-size: 1.5rem;
              color: #856404;
            }
            .warning-content {
              flex: 1;
            }
            .warning-title {
              font-weight: 600;
              color: #856404;
              font-size: 1rem;
              margin-bottom: 5px;
            }
            .warning-description {
              color: #856404;
              font-size: 0.9rem;
              line-height: 1.4;
            }
            .longitudinal-table {
              background: white;
              border-radius: 8px;
              overflow: hidden;
              border: 1px solid #e9ecef;
            }
            .longitudinal-table table {
              width: 100%;
              border-collapse: collapse;
              margin: 0;
              font-size: 0.9rem;
            }
            .longitudinal-table thead th {
              background: #f8f9fa;
              border-bottom: 2px solid #e9ecef;
              font-weight: 600;
              color: #495057;
              padding: 12px 15px;
              text-align: left;
            }
            .longitudinal-table tbody td {
              padding: 12px 15px;
              border-bottom: 1px solid #e9ecef;
              color: #2c3e50;
            }
            .longitudinal-table tbody tr:last-child td {
              border-bottom: none;
            }
            .report-disclaimer {
              background: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
              border-left: 4px solid #007bff;
              margin-top: 25px;
            }
            .report-disclaimer p {
              margin: 0;
              font-size: 0.9rem;
              color: #6c757d;
              line-height: 1.5;
              font-style: italic;
            }
            @media print {
              body { margin: 0; }
              .report-content { max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="report-content">
            ${reportContent.innerHTML}
          </div>
        </body>
        </html>
      `;

      // Write the HTML to the new window
      printWindow.document.write(printHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    } else {
      console.error('Report content not found');
      printWindow.close();
    }
  };

  // Export functions
  const processExportData = () => {
    const exportData = allScansData.map(scan => {
      const analysisResult = scan.analysisResult;
      const consolidatedResult = analysisResult?.consolidated_results?.[0];
      const biomarkers = consolidatedResult?.biomarkers || {};
      const patientInfo = consolidatedResult?.patientInformation || {};

      return {
        // Patient Demographics
        patientId: patientInfo.patientId || scan.patientId || 'N/A',
        patientName: scan.patientName || 'N/A',
        ageAtScan: patientInfo.ageAtScan || scan.patientAge || 'N/A',
        sex: patientInfo.sex || scan.patientSex || 'N/A',
        diagnosis: patientInfo.msSubtype || scan.patientDiagnosis || 'N/A',
        edssScore: patientInfo.edssScore || 'N/A',

        // Scan Information
        timepoint: analysisResult?.timepoint || 'N/A',
        scanDate: analysisResult?.analysis_timestamp ?
          new Date(analysisResult.analysis_timestamp).toISOString().split('T')[0] : 'N/A',
        segmentationMethod: consolidatedResult?.segmentation_method || 'N/A',
        processingTime: analysisResult?.processing_time_seconds || 'N/A',

        // Brain Volume Measurements
        totalBrainVolume_mm3: biomarkers.totalBrainVolume_mm3 || 'N/A',
        totalBrainVolume_mL: biomarkers.totalBrainVolume_mm3 ?
          (Number(biomarkers.totalBrainVolume_mm3) / 1000).toFixed(2) : 'N/A',
        greyMatterVolume_mm3: biomarkers.greyMatterVolume_mm3 || 'N/A',
        greyMatterVolume_mL: biomarkers.greyMatterVolume_mm3 ?
          (Number(biomarkers.greyMatterVolume_mm3) / 1000).toFixed(2) : 'N/A',
        whiteMatterVolume_mm3: biomarkers.whiteMatterVolume_mm3 || 'N/A',
        whiteMatterVolume_mL: biomarkers.whiteMatterVolume_mm3 ?
          (Number(biomarkers.whiteMatterVolume_mm3) / 1000).toFixed(2) : 'N/A',

        // Lesion Biomarkers
        totalLesionCount: biomarkers.totalLesionCount || 'N/A',
        totalLesionVolume_mm3: biomarkers.totalLesionVolume_mm3 || 'N/A',
        totalLesionVolume_mL: biomarkers.totalLesionVolume_mm3 ?
          (Number(biomarkers.totalLesionVolume_mm3) / 1000).toFixed(2) : 'N/A',
        lesionVolumeInWhiteMatter_mm3: biomarkers.lesionVolumeInWhiteMatter_mm3 || 'N/A',
        lesionVolumeInWhiteMatter_mL: biomarkers.lesionVolumeInWhiteMatter_mm3 ?
          (Number(biomarkers.lesionVolumeInWhiteMatter_mm3) / 1000).toFixed(2) : 'N/A',
        lesionVolumeInGreyMatter_mm3: biomarkers.lesionVolumeInGreyMatter_mm3 || 'N/A',
        lesionVolumeInGreyMatter_mL: biomarkers.lesionVolumeInGreyMatter_mm3 ?
          (Number(biomarkers.lesionVolumeInGreyMatter_mm3) / 1000).toFixed(2) : 'N/A',

        // T1 Black Hole Intensity
        averageT1BlackHoleIntensity: biomarkers.averageT1BlackHoleIntensity ?
          Number(biomarkers.averageT1BlackHoleIntensity).toFixed(2) : 'N/A',

        // Atrophy Metrics
        annualizedBrainVolumeLoss_mm3_per_year: biomarkers.annualizedBrainVolumeLoss_mm3_per_year || 'N/A',
        brainAtrophyRate: biomarkers.brainAtrophyRate || biomarkers.atrophyRate || 'N/A',

        // Change from Baseline
        changeFromBaseline_totalLesionVolume_mm3: biomarkers.changeFromBaseline_totalLesionVolume_mm3 || 'N/A',
        changeFromBaseline_totalLesionCount: biomarkers.changeFromBaseline_totalLesionCount || 'N/A',
        changeFromBaseline_totalBrainVolume_mm3: biomarkers.changeFromBaseline_totalBrainVolume_mm3 || 'N/A',
        changeFromBaseline_greyMatterVolume_mm3: biomarkers.changeFromBaseline_greyMatterVolume_mm3 || 'N/A',
        changeFromBaseline_whiteMatterVolume_mm3: biomarkers.changeFromBaseline_whiteMatterVolume_mm3 || 'N/A',
        changeFromBaseline_averageT1BlackHoleIntensity: biomarkers.changeFromBaseline_averageT1BlackHoleIntensity || 'N/A',

        // Additional Metrics
        greyMatterLesionRatio: biomarkers.greyMatterLesionRatio || 'N/A',

        // Timestamps
        analysisTimestamp: analysisResult?.analysis_timestamp || 'N/A',
        createdAt: scan.createdAt || 'N/A',
        updatedAt: scan.updatedAt || 'N/A'
      };
    });

    return exportData;
  };

  const downloadCSV = () => {
    try {
      setExportLoading(true);
      const data = processExportData();

      if (data.length === 0) {
        alert('No data available for export');
        return;
      }

      // Create CSV headers
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row =>
          headers.map(header => {
            const value = row[header];
            // Escape commas and quotes in CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `ms_research_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Error downloading CSV file');
    } finally {
      setExportLoading(false);
    }
  };

  const downloadJSON = () => {
    try {
      setExportLoading(true);
      const data = processExportData();

      if (data.length === 0) {
        alert('No data available for export');
        return;
      }

      // Create comprehensive JSON structure
      const jsonData = {
        exportInfo: {
          exportDate: new Date().toISOString(),
          totalPatients: [...new Set(data.map(item => item.patientId))].length,
          totalScans: data.length,
          dataPoints: data.length * Object.keys(data[0]).length,
          format: 'JSON',
          description: 'Complete processed dataset for clinical trials and academic studies'
        },
        dataFields: {
          patientDemographics: ['patientId', 'patientName', 'ageAtScan', 'sex', 'diagnosis', 'edssScore'],
          lesionBiomarkers: ['totalLesionCount', 'totalLesionVolume_mm3', 'totalLesionVolume_mL', 'lesionVolumeInWhiteMatter_mm3', 'lesionVolumeInGreyMatter_mm3'],
          t1BlackHoleIntensity: ['averageT1BlackHoleIntensity'],
          brainVolumeMeasurements: ['totalBrainVolume_mm3', 'totalBrainVolume_mL', 'greyMatterVolume_mm3', 'whiteMatterVolume_mm3'],
          tissueVolumeData: ['greyMatterVolume_mL', 'whiteMatterVolume_mL', 'lesionVolumeInWhiteMatter_mL', 'lesionVolumeInGreyMatter_mL'],
          longitudinalMetrics: ['changeFromBaseline_totalLesionVolume_mm3', 'changeFromBaseline_totalLesionCount', 'changeFromBaseline_totalBrainVolume_mm3', 'annualizedBrainVolumeLoss_mm3_per_year']
        },
        data: data
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `ms_research_data_${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading JSON:', error);
      alert('Error downloading JSON file');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="reports">
      <div className='custom-card'>
        <div className='custom-card-header'>
          <div className='custom-card-header-text d-flex'>
            <div className='icon-wrapper'>
              <img src={Images.ReportsIconFill} alt="Brain Icon" className="icon" />
            </div>
            <div>
              <h2>
                Reports & Data Export
              </h2>
              <p>Generate  patient summaries and exp  ort research datasets</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className='custom-tabs mt-4'>
        <Tab.Container
          id="reports-tabs"
          defaultActiveKey="patient-summary"
          onSelect={(key) => {
            setActiveTab(key);
            // Only fetch all scans when Research Data Export tab is selected
            if (key === 'research-export' && allScansData.length === 0 && patients.length > 0) {
              fetchAllScansData();
            }
          }}
        >
          <Nav variant="pills" className="reports-nav">
            <Nav.Item>
              <Nav.Link eventKey="patient-summary">Patient Summary</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="research-export">Research Data Export</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="patient-summary">
              <div className='custom-card'>
                <div className='custom-card-header'>
                  <div className='custom-card-header-text'>
                    <h3>
                      Generate Patient Summary
                    </h3>
                    <p>Create a printable clinical summary report for a single patient</p>
                  </div>
                </div>
                <div className='custom-card-body'>
                  {/* Patient Selection */}
                  <div className='patient-selection-section'>
                    <div className='form-group'>
                      <label htmlFor="patientSelect">Select Patient</label>
                      <select
                        id="patientSelect"
                        className='form-control'
                        onChange={(e) => handlePatientSelect(e.target.value)}
                        disabled={loading}
                        value={selectedPatient?.id || ''}
                      >
                        <option value="">Choose a patient...</option>
                        {patients.map(patient => (
                          <option key={patient.id} value={patient.id}>
                            {patient.patientId} - {patient.name} {patient.email ? `(${patient.email})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Patient Details Display */}
                    {selectedPatient && (
                      <div className='patient-details-card'>
                        <div className='patient-details-grid'>
                          <div className='detail-item'>
                            <span className='detail-label'>Patient ID:</span>
                            <span className='detail-value'>
                              {patientScans.length > 0 && patientScans[0]?.analysisResult?.consolidated_results?.[0]?.patientInformation?.patientId || selectedPatient.patientId || selectedPatient.id}
                            </span>
                          </div>
                          <div className='detail-item'>
                            <span className='detail-label'>Age / Sex:</span>
                            <span className='detail-value'>
                              {(() => {
                                const patientInfo = patientScans.length > 0 ? patientScans[0]?.analysisResult?.consolidated_results?.[0]?.patientInformation : null;
                                const latestScan = patientScans.length > 0 ? patientScans[0] : null;
                                const age = patientInfo?.ageAtScan || latestScan?.age || selectedPatient.latestScan?.age || 'N/A';
                                const sex = patientInfo?.sex === 'M' ? 'Male' : patientInfo?.sex === 'F' ? 'Female' :
                                  (latestScan?.sex === 'M' ? 'Male' : latestScan?.sex === 'F' ? 'Female' :
                                    (selectedPatient.latestScan?.sex === 'M' ? 'Male' : selectedPatient.latestScan?.sex === 'F' ? 'Female' : 'N/A'));
                                return `${age} / ${sex}`;
                              })()}
                            </span>
                          </div>
                          <div className='detail-item'>
                            <span className='detail-label'>Diagnosis:</span>
                            <span className='detail-value'>
                              {(() => {
                                const patientInfo = patientScans.length > 0 ? patientScans[0]?.analysisResult?.consolidated_results?.[0]?.patientInformation : null;
                                const latestScan = patientScans.length > 0 ? patientScans[0] : null;
                                return patientInfo?.msSubtype || latestScan?.msSubType || selectedPatient.latestScan?.msSubType || 'Multiple Sclerosis';
                              })()}
                            </span>
                          </div>
                          <div className='detail-item'>
                            <span className='detail-label'>Timepoints:</span>
                            <span className='detail-value'>{patientScans.length} scans</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Generate Report Button */}
                    <div className='generate-button-section'>
                      <button
                        className='btn btn-primary btn-lg'
                        onClick={generateReport}
                        disabled={!selectedPatient || loading}
                      >
                        {/* <span className='icon'>📄</span> */}
                        Generate & Preview Report
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey="research-export">
              <div className='custom-card'>
                <div className='custom-card-header'>
                  <div className='custom-card-header-text'>
                    <h3>
                      Research Data Export
                    </h3>
                    <p>Export complete processed dataset for clinical trials and academic studies</p>
                  </div>
                </div>
                <div className='custom-card-body'>
                  {/* Summary Section */}
                  <div className='export-summary'>
                    <div className='summary-grid'>
                      <div className='summary-item'>
                        <span className='summary-label'>Total Patients:</span>
                        <span className='summary-value'>{scansLoading ? '...' : [...new Set(allScansData.map(scan => scan.patientId))].length}</span>
                      </div>
                      <div className='summary-item'>
                        <span className='summary-label'>Total Scans:</span>
                        <span className='summary-value'>{scansLoading ? '...' : allScansData.length}</span>
                      </div>
                      <div className='summary-item'>
                        <span className='summary-label'>Data Points:</span>
                        <span className='summary-value'>{scansLoading ? '...' : allScansData.length * 30}</span>
                      </div>
                      <div className='summary-item'>
                        <span className='summary-label'>Format:</span>
                        <span className='summary-value'>CSV / JSON</span>
                      </div>
                    </div>
                  </div>

                  {/* Data Preview Table */}
                  <div className='data-preview-section'>
                    <h4>Data Preview</h4>
                    <div className="custom-table-wrapper mb-2">
                      <Table hover responsive className="custom-table">
                        <thead>
                          <tr>
                            <th>Patient ID</th>
                            <th>Timepoint</th>
                            <th>Scan Date</th>
                            <th>Brain Vol (mL)</th>
                            <th>Lesion Vol (mL)</th>
                            <th>T1 Intensity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scansLoading ? (
                            <tr>
                              <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                Loading scan data...
                              </td>
                            </tr>
                          ) : allScansData.slice(0, 5).map((scan, index) => {
                            const analysisResult = scan.analysisResult;
                            const consolidatedResult = analysisResult?.consolidated_results?.[0];
                            const biomarkers = consolidatedResult?.biomarkers || {};
                            const patientInfo = consolidatedResult?.patientInformation || {};

                            return (
                              <tr key={index}>
                                <td>{patientInfo.patientId || scan.patientId || 'N/A'}</td>
                                <td>{analysisResult?.timepoint || 'N/A'}</td>
                                <td>{analysisResult?.analysis_timestamp ?
                                  new Date(analysisResult.analysis_timestamp).toISOString().split('T')[0] : 'N/A'}</td>
                                <td>{biomarkers.totalBrainVolume_mm3 ?
                                  (Number(biomarkers.totalBrainVolume_mm3) / 1000).toFixed(2) : 'N/A'}</td>
                                <td>{biomarkers.totalLesionVolume_mm3 ?
                                  (Number(biomarkers.totalLesionVolume_mm3) / 1000).toFixed(2) : 'N/A'}</td>
                                <td>{biomarkers.averageT1BlackHoleIntensity ?
                                  Number(biomarkers.averageT1BlackHoleIntensity).toFixed(2) : 'N/A'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    <p className='preview-note'>
                      Showing preview of first few records. Full export includes all patients with complete biomarker data.
                    </p>
                  </div>



                  {/* Download Buttons */}
                  <div className='download-section'>
                    <button
                      className='btn btn-dark btn-lg download-btn'
                      onClick={downloadCSV}
                      disabled={exportLoading || allScansData.length === 0 || scansLoading}
                    >
                      Download as CSV
                    </button>
                    <button
                      className='btn btn-dark btn-lg download-btn'
                      onClick={downloadJSON}
                      disabled={exportLoading || allScansData.length === 0 || scansLoading}
                    >
                      Download as JSON
                    </button>
                  </div>

                  {/* Footer Note */}
                  <div className='export-footer'>
                    <p>All exported data is derived from the consolidated_results.json pipeline output.</p>
                  </div>
                </div>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      {/* Report Modal */}
      <CustomModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        size="lg"
        className="report-modal"
        customHeader={
          <div className="report-modal-header">
            <div className="d-flex align-items-center">
              <h4 className="mb-0">Patient Summary Report</h4>
            </div>
            {reportData && (
              <p className="text-muted mb-0 mt-1">
                Generated on {new Date().toLocaleDateString()} for {reportData.patient.name}
              </p>
            )}
          </div>
        }
        footerButtons={[
          {
            label: 'Close',
            variant: 'secondary',
            onClick: () => setShowReportModal(false)
          },
          {
            label: 'Print Report',
            variant: 'primary',
            onClick: printReport,
            className: 'print-btn'
          }
        ]}
      >
        {reportData && (
          <div className='report-content'>
            <div className='report-subtitle'>
              Clinical report for {reportData.patient.name} ({reportData.patient.id})
            </div>

            {/* Clinical Details Table */}
            <div className='report-section mb-3'>
              <div className='preview-table-container'>
                <table className='table clinical-table d-flex align-items-center'>
                  <thead>
                    <tr>
                      <th>Patient ID</th>
                      <th>Name</th>
                      <th>Age / Sex</th>
                      <th>Diagnosis</th>
                      <th>Total Scans</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{reportData.patient.id}</td>
                      <td>{reportData.patient.name}</td>
                      <td>{reportData.patient.age} / {reportData.patient.sex}</td>
                      <td>{reportData.patient.diagnosis}</td>
                      <td>{reportData.patient.totalScans}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Latest Scan Summary Table */}
            <div className='report-section mb-3'>
              <div className='preview-table-container'>
                <div className='report-subtitle '>
                  Latest Scan Summary
                </div>
                <table className='table scan-summary-table d-flex align-items-center'>
                  <thead>
                    <tr>
                      <th>Timepoint</th>
                      <th>Scan Date</th>
                      <th>Segmentation</th>
                      <th>Total Brain Vol (mL)</th>
                      <th>Total Lesion Vol (mL)</th>
                      <th>WM Lesions (mL)</th>
                      <th>GM Lesions (mL)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{reportData.latestScan.timepoint}</td>
                      <td>{reportData.latestScan.scanDate}</td>
                      <td><span className='segmentation-badge'>{reportData.latestScan.segmentation}</span></td>
                      <td>{reportData.latestScan.totalBrainVolume}</td>
                      <td>{reportData.latestScan.totalLesionVolume}</td>
                      <td>{reportData.latestScan.whiteMatterLesions}</td>
                      <td>{reportData.latestScan.greyMatterLesions}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* T1 Black Hole Warning */}
            {reportData.latestScan.t1Intensity && Number(reportData.latestScan.t1Intensity) < 300 && (
              <div className='report-section mb-3'>
                <div className='warning-box'>
                  <div className='warning-content'>
                    <div className='warning-title'>T1 Black Hole Detected</div>
                    <div className='warning-description'>
                      <strong>Average T1 intensity:</strong> {reportData.latestScan.t1Intensity} <strong>(threshold: &lt;300)</strong> Indicates areas of severe tissue damage.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Longitudinal Data */}
            {/* <div className='report-section'>
              <div className='section-header'>
                <h4>Longitudinal Data</h4>
              </div>
              <div className='longitudinal-table'>
                <table className='table'>
                  <thead>
                    <tr>
                      <th>Timepoint</th>
                      <th>Date</th>
                      <th>Brain Vol (mL)</th>
                      <th>Lesion Vol (mL)</th>
                      <th>T1 Intensity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.longitudinalData.map((scan, index) => (
                      <tr key={index}>
                        <td>{scan.timepoint}</td>
                        <td>{scan.date}</td>
                        <td>{scan.brainVolume}</td>
                        <td>{scan.lesionVolume}</td>
                        <td>{scan.t1Intensity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div> */}

            {/* Disclaimer */}
            <div className='report-disclaimer'>
              <p>
                This report was generated by the MS Medical Imaging Platform using AI-powered analysis.
                All measurements are derived from automated segmentation and should be reviewed by a qualified clinician.
              </p>
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
}

